import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Download, UserPlus, Loader2, BellRing } from "lucide-react";
import { MemberFilters, memberDateRange } from "@/components/admin/MemberFilters";
import { MemberTable } from "@/components/admin/MemberTable";
import { MemberActionDialog } from "@/components/admin/MemberActionDialog";
import { BorrowerManageDialog } from "@/components/admin/BorrowerManage";
import { toast } from "sonner";

const EMPTY_FILTERS = { q: "", start: "", end: "" };

export default function Borrowers() {
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [preset, setPreset] = useState(null);
  const [filterError, setFilterError] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [action, setAction] = useState(null);
  const [manageTab, setManageTab] = useState("portal");
  const requestNumber = useRef(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [manageId, setManageId] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    username: "",
    email: "",
    phone: "",
    address: "",
    government_id: "",
    notes: "",
  });
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/borrowers", { params: q ? { q } : {} });
      setList(data);
      return data;
    } catch {
      setList([]);
      toast.error("Could not load members. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const create = async () => {
    if (!form.full_name || !form.phone) {
      toast.error("Full name and phone are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/borrowers", {
        ...form,
        email: form.email || null,
        address: form.address || null,
        government_id: form.government_id || null,
        notes: form.notes || null,
      });
      toast.success("Borrower added");
      setOpen(false);
      setForm({ full_name: "", username: "", email: "", phone: "", address: "", government_id: "", notes: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add borrower");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this borrower? Only possible if they have no loans.")) return;
    try {
      await api.delete(`/borrowers/${id}`);
      toast.success("Borrower removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="borrowers-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-400 font-bold">
            Directory
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white mt-1">
            All Member
          </h1>
          <p className="text-slate-400 text-sm mt-1" data-testid="member-count">
            {list.length} members · search by username, registered phone, name or email.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${API_BASE}/export/borrowers`}
            data-testid="export-borrowers-csv"
            className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-white/10 bg-white/5 text-slate-200 text-sm hover:bg-white/10"
          >
            <Download className="h-4 w-4" /> CSV
          </a>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-borrower-button"
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                <UserPlus className="h-4 w-4 mr-2" /> Add borrower
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#101624] border border-white/10 text-white sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-xl">New borrower</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
                <div className="col-span-2">
                  <Label className="text-xs text-slate-400">Full name *</Label>
                  <Input
                    data-testid="borrower-fullname-input"
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    className="bg-black/30 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Phone *</Label>
                  <Input
                    data-testid="borrower-phone-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+63 917 ..."
                    className="bg-black/30 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Email</Label>
                  <Input
                    data-testid="borrower-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-black/30 border-white/10 text-white"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="new-member-username" className="text-xs text-slate-400">Username (optional)</Label>
                  <Input id="new-member-username" data-testid="borrower-username-input" value={form.username} maxLength={80}
                    onChange={(e) => setForm({ ...form, username: e.target.value })} className="bg-black/30 border-white/10 text-white" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-400">Address</Label>
                  <Input
                    data-testid="borrower-address-input"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="bg-black/30 border-white/10 text-white"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-400">Government ID</Label>
                  <Input
                    data-testid="borrower-govid-input"
                    value={form.government_id}
                    onChange={(e) => setForm({ ...form, government_id: e.target.value })}
                    placeholder="UMID / Passport / Driver's License"
                    className="bg-black/30 border-white/10 text-white"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-400">Notes</Label>
                  <Textarea
                    data-testid="borrower-notes-input"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="bg-black/30 border-white/10 text-white min-h-[70px]"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setOpen(false)}
                  className="text-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={create}
                  disabled={busy}
                  data-testid="save-borrower-button"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Save borrower
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {list.some((b) => b.portal_account?.status === "pending") && (
        <div className="flex items-center gap-3 p-4 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-200 text-sm" data-testid="pending-signups-banner">
          <BellRing className="h-4 w-4" />
          {list.filter((b) => b.portal_account?.status === "pending").length} portal sign-up(s) awaiting approval. Click “Manage” on the highlighted borrower.
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Username, registered phone, name or email…"
            data-testid="borrower-search-input"
            className="pl-10 bg-black/30 border-white/10 text-white"
          />
        </div>
        <Button variant="secondary" onClick={load} data-testid="borrower-search-apply">
          Search
        </Button>
      </div>

      <Card className="card-glow rounded-xl bg-[#111826] border border-white/5 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">
                Name
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">
                Contact
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">
                Loans
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400 text-right">
                Outstanding
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">
                Added
              </TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 py-10">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && list.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 py-10">
                  {q.trim() ? "No members match this search. Try a username or registered phone number." : "No members yet. Click “Add borrower” to begin."}
                </TableCell>
              </TableRow>
            )}
            {list.map((b) => (
              <TableRow
                key={b.id}
                data-testid={`borrower-row-${b.id}`}
                className="border-white/5 hover:bg-emerald-500/5 cursor-pointer"
                onClick={() => navigate(`/borrowers/${b.id}`)}
              >
                <TableCell className="font-semibold text-white">
                  <div data-testid={`member-name-${b.id}`}>{b.full_name}</div>
                  <div data-testid={`member-username-${b.id}`} className="text-xs font-normal text-sky-300 mt-1">{b.username || b.portal_account?.email || b.email || b.phone}</div>
                </TableCell>
                <TableCell className="text-slate-300 text-sm">
                  <div>{b.phone}</div>
                  <div className="text-slate-500 text-xs">{b.email || "—"}</div>
                </TableCell>
                <TableCell className="font-mono text-slate-300">{b.loan_count}</TableCell>
                <TableCell className="font-mono tabular text-right text-emerald-300">
                  {fmtPHP(b.total_outstanding)}
                </TableCell>
                <TableCell className="text-slate-400 text-sm">{fmtDate(b.created_at)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {b.portal_account ? (
                    <span className={`mr-2 text-[10px] uppercase tracking-wider font-bold ${b.portal_account.status === "pending" ? "text-amber-300" : b.portal_account.status === "active" ? "text-emerald-400" : "text-slate-500"}`} data-testid={`portal-status-${b.id}`}>
                      Portal {b.portal_account.status}
                    </span>
                  ) : null}
                  <Button variant="secondary" size="sm" data-testid={`checking-data-${b.id}`}
                    onClick={(e) => { e.stopPropagation(); navigate(`/borrowers/${b.id}`); }}
                    className="mr-2 bg-sky-600/15 text-sky-200 hover:bg-sky-600/25 border border-sky-500/20">
                    <Eye className="h-4 w-4 mr-1.5" /> Checking data
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setManageId(b.id);
                    }}
                    data-testid={`manage-borrower-${b.id}`}
                    className="text-slate-300 hover:text-emerald-300 hover:bg-emerald-500/10"
                  >
                    <Settings2 className="h-4 w-4 mr-1" /> Manage
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(b.id);
                    }}
                    data-testid={`delete-borrower-${b.id}`}
                    className="text-slate-500 hover:text-rose-400 hover:bg-rose-500/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <BorrowerManageDialog
        borrower={list.find((b) => b.id === manageId)}
        onOpenChange={(o) => !o && setManageId(null)}
        onChange={load}
      />
    </div>
  );
}
